import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import { MediaType, UploadProgress } from "../types/media";
import {
  getUploadUrlWithQuota,
  uploadToR2,
  updateStorageUsageAfterUpload,
  StorageQuotaError,
} from "../services/uploadWithQuota";

export function useMediaUploadWithQuota() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const uploadMedia = async (
    courseId: string,
    file: File,
    mediaType: MediaType
  ): Promise<{ mediaId: string; url: string }> => {
    setUploading(true);
    setProgress({
      fileName: file.name,
      progress: 0,
      status: "uploading",
    });

    try {
      const currentUser = auth.currentUser;
      
      console.log('[useMediaUploadWithQuota] Upload initiated');
      console.log('[useMediaUploadWithQuota] File:', {
        name: file.name,
        type: file.type,
        size: file.size,
        courseId: courseId,
        mediaType: mediaType
      });
      
      if (!currentUser) {
        console.error('[useMediaUploadWithQuota] BLOCKED: No authenticated user');
        throw new Error("กรุณา login เข้าระบบก่อนอัปโหลดไฟล์");
      }

      console.log('[useMediaUploadWithQuota] User authenticated:', {
        uid: currentUser.uid,
        email: currentUser.email
      });

      const safeFileName =
        file.name && file.name.trim() !== ""
          ? file.name
          : `file-${Date.now()}.${file.type.split('/')[1] || 'bin'}`;

      const safeFileType =
        file.type && file.type.trim() !== ""
          ? file.type
          : "application/octet-stream";

      console.log('[useMediaUploadWithQuota] Prepared file:', {
        safeFileName,
        safeFileType
      });
      console.log('[useMediaUploadWithQuota] Checking storage quota and getting upload URL...');
      
      const { uploadUrl, publicUrl, key, originalUrl, originalKey, hasIssue } = await getUploadUrlWithQuota(
        safeFileName,
        safeFileType,
        file.size,
        file,
        (progress, message) => {
          setProgress({
            fileName: file.name,
            progress,
            status: "uploading",
            message,
          });
        }
      );

      console.log('[useMediaUploadWithQuota] Upload completed successfully');
      console.log('[useMediaUploadWithQuota] Public URL:', publicUrl);
      console.log('[useMediaUploadWithQuota] Key:', key);
      console.log('[useMediaUploadWithQuota] Original URL:', originalUrl);
      console.log('[useMediaUploadWithQuota] Original Key:', originalKey);
      console.log('[useMediaUploadWithQuota] Has Issue:', hasIssue);

      if (hasIssue) {
        console.warn('[useMediaUploadWithQuota] ⚠️ File uploaded with issues - may need admin review');
      }

      setProgress({
        fileName: file.name,
        progress: 100,
        status: "uploading",
      });

      console.log('[useMediaUploadWithQuota] R2 upload successful!');
      console.log('[useMediaUploadWithQuota] Updating storage usage...');

      const fileDocId = await updateStorageUsageAfterUpload(currentUser.uid, {
        key,
        path: key,
        sizeBytes: file.size,
        mimeType: safeFileType,
        bucket: "edumi-media",
        publicUrl,
      });

      const mediaRef = collection(db, "courses", courseId, "media");
      const mediaDoc = await addDoc(mediaRef, {
        type: mediaType,
        url: publicUrl,
        key,
        originalUrl: originalUrl || null,
        originalKey: originalKey || null,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        downloadable: mediaType === "file",
        uploadedBy: currentUser.uid,
        createdAt: serverTimestamp(),
        fileDocId,
        hasIssue: hasIssue || false,
        issueReported: hasIssue || false,
      });

      console.log('[useMediaUploadWithQuota] ✅ Upload completed successfully!');
      console.log('[useMediaUploadWithQuota] Media ID:', mediaDoc.id);
      console.log('[useMediaUploadWithQuota] Public URL:', publicUrl);
      
      setProgress({
        fileName: file.name,
        progress: 100,
        status: "success",
      });

      setUploading(false);
      
      setTimeout(() => {
        setProgress(null);
      }, 1000);
      
      return { mediaId: mediaDoc.id, url: publicUrl };
    } catch (error: any) {
      console.error('[useMediaUploadWithQuota] ❌ Upload failed!');
      console.error('[useMediaUploadWithQuota] Error type:', error.constructor.name);
      console.error('[useMediaUploadWithQuota] Error message:', error.message);
      console.error('[useMediaUploadWithQuota] Full error:', error);
      
      let errorMessage = error.message || "Upload failed";
      
      if (error instanceof StorageQuotaError) {
        console.error('[useMediaUploadWithQuota] Storage quota exceeded');
        errorMessage = error.message;
      }
      
      setProgress({
        fileName: file.name,
        progress: 0,
        status: "error",
        error: errorMessage,
      });
      setUploading(false);
      throw error;
    }
  };

  return {
    uploadMedia,
    uploading,
    progress,
  };
}
